import { PhosphorTrashButton, PhosphorPencilButton } from "components/Atoms/PhosphorIconButton";
import CultureFlag from "components/Culture/CultureFlag";
import CollapsibleSection from "components/DetailPage/CollapsibleSection";
import InPageEditableFieldWithLabel from "components/MainSectionTemplate/InPageEditableFieldWithLabel";
import { ContactPreview } from "components/PreviewPanel/contact/ContactPreviewPanel";
import { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import React from "react";
import { isNullOrEmptyString } from "utilities";
import ParticipantDebugInfo from "./ParticipantDebugInfo";
import { ButtonPrimary } from "components/Atoms/Button";
import { useMutation } from "@tanstack/react-query";
import { useContact } from "utilities/hookutils";

interface Props {
  lifeCycle: server.dto.DocumentLifeCycles
  participant: server.dto.InvoiceParticipant
  onDeleteClicked: () => void
  onEditClicked: () => void
}

const RecipientsRow = (props: Props) => {
  const { participant } = props;

  const lifeCyclesAllowedToEditViewers: server.dto.DocumentLifeCycles[] = ["draft", "review"];
  const lifeCyclesAllowedToRemoveViewers: server.dto.DocumentLifeCycles[] = ["draft", "review", "pending"];

  const canEdit = lifeCyclesAllowedToEditViewers.includes(props.lifeCycle);
  const canRemove = lifeCyclesAllowedToRemoveViewers.includes(props.lifeCycle);

  const resendMutation = useMutation(window.CoreApi.InvoiceV2.ResendParticipantEmail);

  const resendInvitation = async () => {
    const result = await resendMutation.mutateAsync({ participantId: props.participant.invoiceParticipantId });
    if (result.data.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, result.data.errors[0].message, "mini", "red");
      return;
    }
  }

  const contact = useContact(participant.contact);

  return <div className="flex-column gap">
    <div className="flex gap">
      <div>
        <CultureFlag lcid={contact.culture} size="medium" />
      </div>
      <div className="grow1 flex-column gap">
        <div>
          <ContactPreview
            trigger={<h4>
              <a>
                {contact.name.fullName}
              </a>
            </h4>}
            contactId={contact.contactId}
          />
          <div>
            {participant.email}
          </div>
        </div>

        <ParticipantDebugInfo participant={props.participant} />

        {props.lifeCycle === "pending" && <div>
          <ButtonPrimary
            loading={resendMutation.isLoading}
            disabled={resendMutation.isLoading}
            onClick={resendInvitation}
            label="Resend invitation"
          />
        </div>}
      </div>

      {canRemove && <PhosphorTrashButton
        layout="in-square"
        onClick={props.onDeleteClicked} />}
      {canEdit && <PhosphorPencilButton
        layout="in-square"
        onClick={props.onEditClicked} />}
    </div>
    {canEdit && <div>
      <CollapsibleSection
        title={RESX.Invitation.resxInvitation}
        collapsible="startClosed"
        uniqueRowId={`esign-viewer-${participant.invoiceParticipantId}`}
      >
        <div className="main-section-template-grid">
          <InPageEditableFieldWithLabel
            compact={false}
            field={{
              key: "subject",
              label: "Subject",
              isEmpty: isNullOrEmptyString(participant.invitationSubject),
              render: () => <div>{participant.invitationSubject}</div>
            }}
          />

          <InPageEditableFieldWithLabel
            compact={false}
            field={{
              key: "body",
              label: "Body",
              isEmpty: isNullOrEmptyString(participant.invitationBody),
              render: () => <OptionalTextAreaDetails>{participant.invitationBody}</OptionalTextAreaDetails>
            }}
          />
        </div>
      </CollapsibleSection>
    </div>}
  </div>;
};

export default RecipientsRow;